import React, { useState, useEffect, useRef } from 'react';
import { SelectChangeEvent, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Checkbox, Button, TextField, MenuItem, Tabs, Tab } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import { guidGenerator } from '../../utils/utils';
import cn from 'clsx';
import { TextComponent, SelectComponent, TooltipComponent } from '../uiComponents';
import { jsonEditorStyles } from './jsonEditorStyles';
import { useSelector, useDispatch } from 'react-redux';
import {
  getTemplatizationFields,
  getTemplatizationEnvironments,
  setTemplatizationField,
  getNodeData,
  getTemplatedComponents,
  setTemplatedComponent,
  setNodeData,
  deleteTemplatedComponent,
  deleteTemplateFieldById,
  setTemplatizationEnvironment,
  getFlowcharts,
  deleteTemplatizationEnvironment,
} from '../../store';
import { OptionProps } from '../../view-models';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import DeleteIcon from '@mui/icons-material/Delete';
import { cloneDeep, isEqual, StringIterator } from 'lodash';
import StateManagedSelect from 'react-select/dist/declarations/src/stateManager';
import { setEnvironmentData } from 'worker_threads';
import jsonConstants from '../../constants/jsonConstants';
import defaultEnvs from '../../constants/defaultEnvs';

const extraStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: '0 0.5rem 0.5rem 0.5rem',
      border: '2px solid #010101',
      borderRadius: '2px',
      boxShadow: '5px 5px 5px grey',
      marginBottom: '2rem',
      maxHeight: '96%',
      overflowY: 'auto',
    },
    containerElement: {
      flex: 'none',
      minHeight: 'min-content',
    },
    containerList: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    },
    leftMargin: {
      marginLeft: '1rem',
    },
    topMargin: {
      marginTop: '0.5rem',
    },
    overflows: {
      overflowY: 'auto',
    },
    fillParent: {
      maxHeight: '100%',
    },
    flexFill: {
      flex: '1',
    },
  }),
);

const checkNameValidity = (
  name: string,
  id: string,
  allSet: any,
  currentlyEditingNew: boolean,
  setError: Function,
  descCased: string,
) => {
  if (currentlyEditingNew) {
    if (name === '') {
      setError(`${descCased} name must not be empty`);
    } else if (Object.keys(allSet).some((k: string) => allSet[k].name === name)) {
      setError(`Another ${descCased.toLowerCase()} already exists with the specified name`);
    } else {
      setError('');
    }
  } else {
    if (name === '') {
      setError(`${descCased} name must not be empty`);
    } else if (
      Object.keys(allSet)
        .filter((k: string) => {
          return allSet[k].id !== id;
        })
        .some((k: string) => allSet[k].name === name)
    ) {
      setError(`Another ${descCased.toLowerCase()} already exists with the specified name`);
    } else {
      setError('');
    }
  }
};

interface EditingNodeField {
  nodeId: string;
  fieldPaths: string[];
}

interface EditingSetting {
  id: string;
  name: string;
  nodeFields: { [index: string]: EditingNodeField };
}

interface EditingEnv {
  id: string;
  name: string;
  settings: { [index: string]: string };
  linked?: boolean;
  readonlyDefaultSettings?: boolean;
}

const AppSettingsComponent = () => {
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [newEditingSetting, setNewEditingSetting] = useState({
    name: '',
  });
  const [currentEditingSetting, setCurrentEditingSetting] = useState<EditingSetting>({
    id: '',
    name: '',
    nodeFields: {},
  });
  const [newEditingEnv, setNewEditingEnv] = useState({
    name: '',
    settings: {},
  });
  const [currentEditingEnv, setCurrentEditingEnv] = useState<EditingEnv>({
    id: '',
    name: '',
    settings: {},
  });
  const [currentlyEditingNewSetting, setCurrentlyEditingNewSetting] = useState<boolean>(true);
  const [currentlyEditingNewEnv, setCurrentlyEditingNewEnv] = useState<boolean>(true);

  const [settingNameError, setSettingNameError] = useState<string>(
    currentlyEditingNewSetting
      ? newEditingSetting.name
        ? ''
        : 'Setting name must not be empty'
      : currentEditingSetting.name
      ? ''
      : 'Setting name must not be empty',
  );
  const [envNameError, setEnvNameError] = useState<string>(
    currentlyEditingNewEnv
      ? newEditingEnv.name
        ? ''
        : 'Environment name must not be empty'
      : currentEditingEnv.name
      ? ''
      : 'Environment name must not be empty',
  );

  const classes = jsonEditorStyles();
  const extraClasses = extraStyles();
  const dispatch = useDispatch();
  const allFields = useSelector(getTemplatizationFields);
  const allEnvironments = useSelector(getTemplatizationEnvironments);
  const allFlowcharts = useSelector(getFlowcharts);
  const nodeData = useSelector(getNodeData);
  const templatedComponents = useSelector(getTemplatedComponents);

  useEffect(() => {
    checkNameValidity(
      currentlyEditingNewEnv ? newEditingEnv.name : currentEditingEnv.name,
      currentlyEditingNewEnv ? '' : currentEditingEnv.id,
      allEnvironments,
      currentlyEditingNewEnv,
      setEnvNameError,
      'Environment',
    );
  }, [currentEditingEnv, newEditingEnv, currentlyEditingNewEnv]);

  useEffect(() => {
    checkNameValidity(
      currentlyEditingNewSetting ? newEditingSetting.name : currentEditingSetting.name,
      currentlyEditingNewSetting ? '' : currentEditingSetting.id,
      allFields,
      currentlyEditingNewSetting,
      setSettingNameError,
      'Setting',
    );
  }, [currentEditingSetting, newEditingSetting, currentlyEditingNewSetting]);

  return (
    <div className={cn(classes.flexCol, extraClasses.fillParent, classes.flex)}>
      <Tabs
        value={currentTabIndex}
        onChange={(e, newValue) => {
          setCurrentTabIndex(newValue);
        }}
      >
        <Tab label="Settings" />
        <Tab label="Environments" />
      </Tabs>
      {currentTabIndex === 0 && (
        <div className={classes.flexRow}>
          <div className={cn(classes.flexCol, classes.flex)}>
            {currentlyEditingNewSetting && (
              <div>
                <div>
                  <Typography sx={{ mt: 1 }} variant="h6">
                    Add a Setting
                  </Typography>
                </div>
                <div>
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                      checkNameValidity(e.target.value, '', allFields, true, setSettingNameError, 'Setting');
                      setNewEditingSetting({
                        ...newEditingSetting,
                        name: e.target.value,
                      });
                    }}
                    data={{
                      value: newEditingSetting.name || '',
                      label: 'Setting Name',
                      error: !!settingNameError,
                      helperText: settingNameError,
                    }}
                  />
                </div>
                <div>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                      const _newId = `setting-${guidGenerator()}`;
                      dispatch(
                        setTemplatizationField({
                          [_newId]: {
                            id: _newId,
                            name: newEditingSetting.name,
                            nodeFields: {},
                          },
                        }),
                      );
                      setNewEditingSetting({
                        name: '',
                      });
                    }}
                    disabled={!!settingNameError}
                  >
                    Add Setting
                  </Button>
                </div>
              </div>
            )}
            {!currentlyEditingNewSetting && (
              <div>
                <div>
                  <Typography variant="h6">Edit Setting</Typography>
                </div>
                <div>
                  <TextComponent
                    changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                      checkNameValidity(
                        e.target.value,
                        currentEditingSetting.id,
                        allFields,
                        false,
                        setSettingNameError,
                        'Setting',
                      );
                      setCurrentEditingSetting({
                        ...currentEditingSetting,
                        name: e.target.value,
                      });
                    }}
                    data={{
                      value: currentEditingSetting.name || '',
                      label: 'Setting Name',
                      error: !!settingNameError,
                      helperText: settingNameError,
                      disabled:
                        Object.keys(jsonConstants.defaultAppSettingFields).includes(currentEditingSetting.id) &&
                        jsonConstants.defaultAppSettingFields[
                          currentEditingSetting.id as keyof typeof jsonConstants.defaultAppSettingFields
                        ].readonly,
                    }}
                  />
                </div>
                <div>
                  <Typography>Components currently using this setting:</Typography>
                </div>
                <div className={classes.flexCol}>
                  {Object.keys(currentEditingSetting.nodeFields)
                    .map((nodeField: any, idxa: number) => {
                      return allFlowcharts[templatedComponents[nodeField].flowchart].nodes.find((nd: any) => {
                        return nd.id === nodeField;
                      }).data.label;
                    })}
                </div>

                <div>
                  {(!Object.keys(jsonConstants.defaultAppSettingFields).includes(currentEditingSetting.id) ||
                    !jsonConstants.defaultAppSettingFields[
                      currentEditingSetting.id as keyof typeof jsonConstants.defaultAppSettingFields
                    ].readonly) && (
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      onClick={() => {
                        dispatch(
                          setTemplatizationField({
                            [currentEditingSetting.id]: {
                              id: currentEditingSetting.id,
                              name: currentEditingSetting.name,
                              nodeFields: { ...currentEditingSetting.nodeFields },
                            },
                          }),
                        );
                        setNewEditingSetting({
                          name: '',
                        });
                      }}
                      disabled={
                        !!settingNameError || isEqual(currentEditingSetting, allFields[currentEditingSetting.id])
                      }
                    >
                      Update Setting
                    </Button>
                  )}
                </div>
                <div>
                  {Object.keys(jsonConstants.defaultAppSettingFields).includes(currentEditingSetting.id) &&
                    jsonConstants.defaultAppSettingFields[
                      currentEditingSetting.id as keyof typeof jsonConstants.defaultAppSettingFields
                    ].readonly && (
                      <Typography sx={{ mt: 3, mb: 2 }}>
                        This setting can have values assigned to it in environments, but cannot be renamed or deleted.
                      </Typography>
                    )}
                </div>
              </div>
            )}
          </div>
          <div className={cn(classes.flexCol, classes.flex, extraClasses.leftMargin)}>
            {Object.keys(allFields).length > 0 && (
              <div className={cn(classes.flexCol, extraClasses.container, extraClasses.topMargin)}>
                <Typography variant="h6">All Settings</Typography>
                <div className={extraClasses.containerList}>
                  {Object.keys(allFields).map((settingsField: string, idx: number) => {
                    return (
                      <div
                        className={cn(classes.flexRow, extraClasses.containerElement)}
                        key={idx}
                        style={{ alignItems: 'center' }}
                      >
                        <Typography sx={{ mx: 2, my: 1 }}>{allFields[settingsField].name}</Typography>
                        <div
                          className={classes.flexRow}
                          style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 'auto',
                            minWidth: '80px',
                          }}
                        >
                          <ModeEditOutlineSharpIcon
                            onClick={() => {
                              if (
                                currentlyEditingNewSetting ||
                                currentEditingSetting.id !== allFields[settingsField].id
                              ) {
                                setCurrentEditingSetting({
                                  id: allFields[settingsField].id,
                                  name: allFields[settingsField].name,
                                  nodeFields: Object.keys(allFields[settingsField].nodeFields).reduce(
                                    (nFields: { [index: string]: any }, nFieldId) => {
                                      nFields[nFieldId] = {
                                        nodeId: allFields[settingsField].nodeFields[nFieldId].nodeId,
                                        fieldPaths: [...allFields[settingsField].nodeFields[nFieldId].fieldPaths],
                                      };
                                      return nFields;
                                    },
                                    {},
                                  ),
                                });
                                setCurrentlyEditingNewSetting(false);
                              }
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                          {(!Object.keys(jsonConstants.defaultAppSettingFields).includes(settingsField) ||
                            !jsonConstants.defaultAppSettingFields[
                              settingsField as keyof typeof jsonConstants.defaultAppSettingFields
                            ].readonly) && (
                            <DeleteIcon
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Are you sure you want to delete the setting ${allFields[settingsField].name}? This cannot be undone. Any fields in components or values set in environments for this setting will be removed.`,
                                  )
                                ) {
                                  let deleteTargetId = allFields[settingsField].id;
                                  if (!currentlyEditingNewSetting && currentEditingSetting.id === deleteTargetId) {
                                    setCurrentEditingSetting({
                                      id: '',
                                      name: '',
                                      nodeFields: {},
                                    });
                                    setNewEditingSetting({
                                      name: '',
                                    });
                                    setCurrentlyEditingNewSetting(true);
                                  }
                                  Object.keys(allFields[settingsField].nodeFields).forEach((nodeId: any) => {
                                    const _nodeData = cloneDeep(nodeData[nodeId]);
                                    let nodeField = _nodeData;
                                    allFields[settingsField].nodeFields[nodeId].fieldPaths.forEach((fPath: string) => {
                                      const fPathSegments = fPath.split('.');
                                      fPathSegments.slice(0, -1).forEach((fps: string) => {
                                        nodeField = nodeField[fps];
                                      });
                                      nodeField[fPathSegments[fPathSegments.length - 1]] = {
                                        value: '',
                                        templatized: false,
                                      };
                                    });
                                    dispatch(
                                      setNodeData({
                                        [nodeId]: cloneDeep(_nodeData),
                                      }),
                                    );
                                    if (
                                      templatedComponents[nodeId].settings.filter((sfId: string) => {
                                        return sfId !== allFields[settingsField].id;
                                      }).length === 0
                                    ) {
                                      dispatch(deleteTemplatedComponent(nodeId));
                                    } else {
                                      dispatch(
                                        setTemplatedComponent({
                                          [nodeId]: {
                                            ...templatedComponents[nodeId],
                                            settings: [
                                              ...templatedComponents[nodeId].settings.filter((sfId: string) => {
                                                return sfId !== allFields[settingsField].id;
                                              }),
                                            ],
                                          },
                                        }),
                                      );
                                    }
                                  });
                                  dispatch(deleteTemplateFieldById(deleteTargetId));
                                }
                              }}
                            />
                          )}
                          {Object.keys(jsonConstants.defaultAppSettingFields).includes(settingsField) &&
                            jsonConstants.defaultAppSettingFields[
                              settingsField as keyof typeof jsonConstants.defaultAppSettingFields
                            ].tooltip && (
                              <div
                                style={{
                                  minWidth: '24px',
                                  minHeight: '24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <TooltipComponent
                                  title={settingsField}
                                  desc={
                                    jsonConstants.defaultAppSettingFields[
                                      settingsField as keyof typeof jsonConstants.defaultAppSettingFields
                                    ].tooltip
                                  }
                                />
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!currentlyEditingNewSetting && (
              <Button
                variant="contained"
                onClick={() => {
                  setCurrentlyEditingNewSetting(true);
                }}
              >
                New Setting
              </Button>
            )}
          </div>
        </div>
      )}
      {currentTabIndex === 1 && (
        <div className={cn(classes.flexRow, extraClasses.flexFill)}>
          {currentlyEditingNewEnv && (
            <div className={cn(classes.flexCol, classes.flex, classes.overFlowYScroll)}>
              <Typography sx={{ mt: 1 }} variant="h6">
                Add an Environment
              </Typography>
              <TextComponent
                changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  checkNameValidity(e.target.value, '', allEnvironments, true, setEnvNameError, 'Environment');
                  setNewEditingEnv({
                    ...newEditingEnv,
                    name: e.target.value,
                  });
                }}
                data={{
                  value: newEditingEnv.name || '',
                  label: 'Environment Name',
                  error: !!envNameError,
                  helperText: envNameError,
                }}
              />
              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                onClick={() => {
                  const _newId = `env-${guidGenerator()}`;
                  const _newName = newEditingEnv.name;
                  dispatch(
                    setTemplatizationEnvironment({
                      [_newId]: {
                        id: _newId,
                        name: _newName,
                        settings: {},
                        linked: false,
                      },
                    }),
                  );
                  setNewEditingEnv({
                    name: '',
                    settings: {},
                  });
                  setCurrentEditingEnv({
                    id: _newId,
                    name: _newName,
                    settings: {},
                  });
                  setCurrentlyEditingNewEnv(false);
                }}
                disabled={!!envNameError}
              >
                Add Environment
              </Button>
            </div>
          )}
          {!currentlyEditingNewEnv && (
            <div className={cn(classes.flexCol, classes.flex, classes.overFlowYScroll, classes.mb1)}>
              <div>
                <Typography sx={{ mt: 1 }} variant="h6">
                  Edit Environment
                </Typography>
              </div>
              <div>
                <TextComponent
                  changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                    checkNameValidity(
                      e.target.value,
                      currentEditingEnv.id,
                      allEnvironments,
                      false,
                      setEnvNameError,
                      'Environment',
                    );
                    setCurrentEditingEnv({
                      ...currentEditingEnv,
                      name: e.target.value,
                    });
                  }}
                  data={{
                    value: currentEditingEnv.name || '',
                    label: 'Environment Name',
                    error: !!envNameError,
                    helperText: envNameError,
                    disabled:
                      Object.keys(defaultEnvs).includes(currentEditingEnv.id) &&
                      defaultEnvs[currentEditingEnv.id as keyof typeof defaultEnvs].readonlyDefaultSettings,
                  }}
                />
              </div>

              <div className={cn(classes.flexCol, extraClasses.overflows)}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell key="theader-name">Setting</TableCell>
                      <TableCell key="theader-value">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(allFields).map((fieldId: string, fidx: number) => {
                      return (
                        <TableRow key={fidx}>
                          <TableCell key={`${fidx}-name`}>
                            <Typography>{allFields[fieldId].name}</Typography>
                          </TableCell>
                          <TableCell key={`${fidx}-value`}>
                            <TextComponent
                              changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                                if (e.target.value) {
                                  setCurrentEditingEnv({
                                    ...currentEditingEnv,
                                    settings: {
                                      ...currentEditingEnv.settings,
                                      [fieldId]: e.target.value,
                                    },
                                  });
                                } else {
                                  setCurrentEditingEnv({
                                    ...currentEditingEnv,
                                    settings: Object.keys(currentEditingEnv.settings).reduce(
                                      (keptKeys: any, curKey) => {
                                        if (curKey !== e.target.value) {
                                          keptKeys[curKey] = currentEditingEnv.settings[curKey];
                                        }
                                        return keptKeys;
                                      },
                                      {},
                                    ),
                                  });
                                }
                              }}
                              data={{
                                value: currentEditingEnv.settings[fieldId] ? currentEditingEnv.settings[fieldId] : '',
                                label: 'Setting Value',
                                disabled:
                                  Object.keys(jsonConstants.defaultAppSettingFields).includes(fieldId) &&
                                  jsonConstants.defaultAppSettingFields[
                                    fieldId as keyof typeof jsonConstants.defaultAppSettingFields
                                  ].readonly &&
                                  Object.keys(defaultEnvs).includes(currentEditingEnv.id) &&
                                  defaultEnvs[currentEditingEnv.id as keyof typeof defaultEnvs].readonlyDefaultSettings,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div>
                <Button
                  variant="contained"
                  type="submit"
                  onClick={() => {
                    dispatch(
                      setTemplatizationEnvironment({
                        [currentEditingEnv.id]: cloneDeep(currentEditingEnv),
                      }),
                    );
                  }}
                  disabled={!!envNameError || isEqual(currentEditingEnv, allEnvironments[currentEditingEnv.id])}
                  sx={{ m: 2 }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
          <div className={cn(classes.flexCol, classes.flex, extraClasses.leftMargin)} style={{ minWidth: '200px' }}>
            {Object.keys(allEnvironments).length > 0 && (
              <div className={cn(classes.flexCol, extraClasses.container, extraClasses.topMargin)}>
                <Typography variant="h6">Environments</Typography>
                <div className={extraClasses.containerList}>
                  {Object.keys(allEnvironments).map((envId: string, idx: number) => {
                    return (
                      <div
                        className={cn(classes.flexRow, extraClasses.containerElement)}
                        key={idx}
                        style={{ alignItems: 'center' }}
                      >
                        <Typography sx={{ mx: 2, my: 1 }}>{allEnvironments[envId].name}</Typography>
                        <div
                          className={classes.flexRow}
                          style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 'auto',
                            minWidth: '80px',
                          }}
                        >
                          <ModeEditOutlineSharpIcon
                            onClick={() => {
                              if (currentlyEditingNewEnv || currentEditingEnv.id !== allEnvironments[envId].id) {
                                setCurrentEditingEnv(cloneDeep(allEnvironments[envId]));
                                setCurrentlyEditingNewEnv(false);
                              }
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                          {!Object.keys(defaultEnvs).includes(envId) && (
                            <DeleteIcon
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Are you sure you want to delete the environment ${allEnvironments[envId].name}? All setting values configured for this environment will be lost.`,
                                  )
                                ) {
                                  let deleteTargetId = allEnvironments[envId].id;
                                  if (!currentlyEditingNewEnv && currentEditingEnv.id === deleteTargetId) {
                                    setCurrentEditingEnv({
                                      id: '',
                                      name: '',
                                      settings: {},
                                    });
                                    setNewEditingEnv({
                                      name: '',
                                      settings: {},
                                    });
                                    setCurrentlyEditingNewEnv(true);
                                  }
                                  dispatch(deleteTemplatizationEnvironment(deleteTargetId));
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {!currentlyEditingNewEnv && (
              <Button
                variant="contained"
                onClick={() => {
                  setCurrentlyEditingNewEnv(true);
                }}
              >
                New Environment
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSettingsComponent;
